/**
 * LandingPage - Public Landing Page
 * Extracted from App.js for better code organization
 */

import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  QrCode, Building2, Users, Check, Star, ArrowRight, Phone, MapPin, Play, Zap,
  TrendingUp, Gift, Eye, Sparkles,
  Instagram, Loader2, Home, X, ChevronDown, ChevronLeft, ChevronRight, MessageCircle,
  Calendar, Menu
} from "lucide-react";
import { toast } from "../../components/ui/sonner";
import useAuthStore from "../../store/useAuthStore";
import Logo from "../../components/common/Logo";
import LocaleSelector from "../../components/common/LocaleSelector";
import SEOHead from "../../components/common/SEOHead";
import DemoVideoModal from "../../components/common/DemoVideoModal";
import HeroPhoneMockup from "../../components/landing/HeroPhoneMockup";
import useLocaleStore from "../../store/useLocaleStore";
// Heavy below-the-fold sections are code-split so they don't bloat the
// initial landing bundle (and are never downloaded by logged-in users).
const FeaturesShowcase = lazy(() => import("../../components/landing/FeaturesShowcase"));
const KitCampaignSection = lazy(() => import("../../components/landing/KitCampaignSection"));
const GallerySection = lazy(() => import("../../components/landing/GallerySection"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { getCurrencySymbol, formatPrice } = useLocaleStore();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showKitExample, setShowKitExample] = useState(false);
  const [kitMedia, setKitMedia] = useState({ image: null, video: null });
  // Full kit-campaign config used to drive the pricing-cards badges + round
  // promo sticker. Fetched once on mount (not lazy) so the cards already know
  // whether to render the campaign integration on first paint.
  const [kitCampaign, setKitCampaign] = useState(null);
  const [showcaseCards, setShowcaseCards] = useState(null);
  const [howItWorks, setHowItWorks] = useState(null);
  const [hero, setHero] = useState(null);
  const [featureCards, setFeatureCards] = useState(null);
  const [featuresHeader, setFeaturesHeader] = useState(null);
  const [demoVideoEnabled, setDemoVideoEnabled] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Fetch admin-editable hero + showcase + how-it-works content (single round-trip).
  // The current i18n locale is appended so the public Hero matches the page language.
  // When `?preview_landing=1` is present, read the draft from localStorage
  // instead — used by the admin panel to preview unsaved changes.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("preview_landing") === "1") {
      try {
        const raw = localStorage.getItem("imocode_landing_preview");
        if (raw) {
          const draft = JSON.parse(raw);
          // Expire previews after 30 minutes for safety.
          if (Date.now() - (draft.ts || 0) < 30 * 60 * 1000) {
            setShowcaseCards(draft.showcase_cards || []);
            setHowItWorks(draft.how_it_works || []);
            // Preview can carry either a single resolved hero or a locales dict.
            // Pick the entry matching the current i18n language with PT fallback.
            const pickedHero =
              (draft.hero_locales &&
                (draft.hero_locales[i18n.language] ||
                  draft.hero_locales.pt ||
                  Object.values(draft.hero_locales)[0])) ||
              draft.hero ||
              null;
            setHero(pickedHero);
            setFeatureCards(draft.feature_cards || []);
            setFeaturesHeader(draft.features_header || null);
            setIsPreviewMode(true);
            return;
          }
        }
      } catch {
        /* fall through to live fetch */
      }
    }
    const backend = process.env.REACT_APP_BACKEND_URL;
    const locale = encodeURIComponent(i18n.language || "pt");
    fetch(`${backend}/api/landing/settings?locale=${locale}`)
      .then((r) => r.json())
      .then((d) => {
        setShowcaseCards(d?.showcase_cards || []);
        setHowItWorks(d?.how_it_works || []);
        setHero(d?.hero || null);
        setFeatureCards(d?.feature_cards || []);
        setFeaturesHeader(d?.features_header || null);
      })
      .catch(() => {
        setShowcaseCards([]);
        setHowItWorks([]);
        setHero(null);
        setFeatureCards([]);
        setFeaturesHeader(null);
      });
  }, [i18n.language]);

  // Fetch demo-video config to decide whether to render the "Ver Demo" button.
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backend}/api/public/config/demo-video`)
      .then((r) => r.json())
      .then((d) => {
        const hasId = !!(d?.desktop_vimeo_id || d?.mobile_vimeo_id);
        setDemoVideoEnabled(!!d?.enabled && hasId);
      })
      .catch(() => setDemoVideoEnabled(false));
  }, []);

  // Eagerly fetch the kit-campaign config so the pricing cards know whether to
  // render the kit badges/sticker on first paint. We also derive kitMedia from
  // this same response, so the "Ver exemplo do kit" modal opens instantly.
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backend}/api/kit-campaign/v2/config`)
      .then((r) => r.json())
      .then((d) => {
        setKitCampaign(d || null);
        setKitMedia({
          image: d?.example_image_url || null,
          video: d?.example_video_url || null,
        });
      })
      .catch(() => setKitCampaign(null));
  }, []);
  const [salesForm, setSalesForm] = useState({
    name: '',
    phone: '',
    email: '',
    region: '',
    type: '',
    contactTime: ''
  });
  const [submittingSales, setSubmittingSales] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [, forceUpdate] = useState(0);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => forceUpdate(n => n + 1);
    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, [i18n]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSalesSubmit = async (e) => {
    e.preventDefault();
    setSubmittingSales(true);
    try {
      // Save lead to database
      await axios.post(`${API}/sales-leads`, salesForm);
      // Redirect to Calendly
      window.open('https://calendly.com/imocode', '_blank');
      setShowSalesModal(false);
      setSalesForm({ name: '', phone: '', email: '', region: '', type: '', contactTime: '' });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.log('Error saving sales lead');
      // Still redirect to Calendly even if save fails
      window.open('https://calendly.com/imocode', '_blank');
      setShowSalesModal(false);
    } finally {
      setSubmittingSales(false);
    }
  };

  // Memoize translated content to update on language change
  const currentLang = i18n.language;

  // --- Dynamic testimonials from API + auto-rotate -------------------------
  const fallbackTestimonials = [
    {
      text: t('landing.testimonials.1.text'),
      name: "Miguel Ferreira",
      role: t('landing.testimonials.1.role'),
      company: "Century 21 Lisboa",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      rating: 5,
    },
    {
      text: t('landing.testimonials.2.text'),
      name: "Sofia Martins",
      role: t('landing.testimonials.2.role'),
      company: "ERA Porto",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
      rating: 5,
    },
    {
      text: t('landing.testimonials.3.text'),
      name: "Carlos Santos",
      role: t('landing.testimonials.3.role'),
      company: "Remax Algarve",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      rating: 5,
    },
    {
      text: "A IMOCODE mudou completamente a forma como apresento os imóveis. Os clientes ficam impressionados com o profissionalismo e eu poupo horas em chamadas de qualificação.",
      name: "Ana Ribeiro",
      role: "Broker Independente",
      company: "Coimbra",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face",
      rating: 5,
    },
    {
      text: "Investi numa subscrição anual e em 2 meses tive retorno total. Os relatórios de visitas e o follow-up automático fazem toda a diferença na conversão.",
      name: "Pedro Almeida",
      role: "CEO",
      company: "Imobiliária Costa Verde",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face",
      rating: 5,
    },
  ];
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [autoRotatePaused, setAutoRotatePaused] = useState(false);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/testimonials`, {
          params: { language: currentLang || "pt" },
        });
        const list = (res.data?.testimonials || []).map((t) => ({
          text: t.text,
          name: t.name,
          role: t.role,
          company: t.company,
          image: t.image_url,
          rating: t.rating || 5,
        }));
        if (list.length > 0) setTestimonials(list);
      } catch {
        /* keep fallback */
      }
    };
    fetchTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang]);

  // Auto-rotate every 3 seconds (paused on hover)
  useEffect(() => {
    if (autoRotatePaused || testimonials.length <= 1) return;
    const id = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 3000);
    return () => clearInterval(id);
  }, [autoRotatePaused, testimonials.length]);

  // Reset active index if it goes out of range when list changes
  useEffect(() => {
    if (activeTestimonial >= testimonials.length) setActiveTestimonial(0);
  }, [testimonials.length, activeTestimonial]);

  const defaultFaqs = useMemo(() => ([
    { q: t('landing.faq.1.q'), a: t('landing.faq.1.a') },
    { q: t('landing.faq.2.q'), a: t('landing.faq.2.a') },
    { q: t('landing.faq.3.q'), a: t('landing.faq.3.a') },
    { q: t('landing.faq.4.q'), a: t('landing.faq.4.a') },
    { q: t('landing.faq.5.q'), a: t('landing.faq.5.a') },
    { q: t('landing.faq.6.q'), a: t('landing.faq.6.a') },
    { q: t('landing.faq.7.q'), a: t('landing.faq.7.a') },
    { q: t('landing.faq.8.q'), a: t('landing.faq.8.a') },
    { q: t('landing.faq.9.q'), a: t('landing.faq.9.a') },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [t]);

  // FAQs are now managed in the admin panel. We fetch the active list from
  // the backend; if the call fails or the list is empty (e.g. very first
  // boot before seed) we fall back to the i18n defaults so the section is
  // never empty.
  const [faqList, setFaqList] = useState(null);
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backend}/api/public/faqs`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.items?.length) {
          setFaqList(d.items.map((it) => ({ q: it.question, a: it.answer })));
        }
      })
      .catch(() => {});
  }, []);
  const faqs = faqList || defaultFaqs;

  const stats = [
    { value: "+127", label: t('landing.stats.agents') },
    { value: "+842", label: t('landing.stats.qrCodes') },
    { value: "+1.790", label: t('landing.stats.scans') },
    { value: "+318", label: t('landing.stats.leadsCaptured') },
  ];

  return (
    <div className="min-h-screen bg-white font-['DM_Sans',sans-serif]">
      <SEOHead
        title="IMOCODE — QR Codes e Gestão Imobiliária para Agentes"
        description="Plataforma SaaS para agentes imobiliários: QR Codes personalizados, captura automática de leads, gestão de imóveis, cartão digital e impressão profissional de etiquetas."
        path="/"
      />
      {isPreviewMode && (
        <div
          data-testid="landing-preview-banner"
          className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white shadow-md"
        >
          <div className="container mx-auto px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 font-semibold">
              <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse" />
              Modo Pré-visualização — alterações não guardadas
            </span>
            <button
              onClick={() => window.close()}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-bold transition"
              data-testid="landing-preview-close"
            >
              Fechar pré-visualização
            </button>
          </div>
        </div>
      )}
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label="Ir para o topo" data-testid="landing-logo-link" className="cursor-pointer transition-opacity hover:opacity-80">
            <Logo className="h-8 sm:h-10 w-auto" />
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="#funcionalidades" className={`font-medium transition ${isScrolled ? 'text-gray-700 hover:text-[#1a56db]' : 'text-gray-700 hover:text-[#1a56db]'}`}>{t('landing.nav.features', 'Funcionalidades')}</a>
            <a href="#como-funciona" className={`font-medium transition ${isScrolled ? 'text-gray-700 hover:text-[#1a56db]' : 'text-gray-700 hover:text-[#1a56db]'}`}>{t('landing.nav.howItWorks', 'Como Funciona')}</a>
            <a href="#galeria" className={`font-medium transition ${isScrolled ? 'text-gray-700 hover:text-[#1a56db]' : 'text-gray-700 hover:text-[#1a56db]'}`}>Galeria</a>
            <a href="#testemunhos" className={`font-medium transition ${isScrolled ? 'text-gray-700 hover:text-[#1a56db]' : 'text-gray-700 hover:text-[#1a56db]'}`}>{t('landing.nav.testimonials', 'Testemunhos')}</a>
            <a href="#precos" className={`font-medium transition ${isScrolled ? 'text-gray-700 hover:text-[#1a56db]' : 'text-gray-700 hover:text-[#1a56db]'}`}>{t('landing.nav.pricing', 'Preços')}</a>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Locale Selector — desktop only; mobile users access it via the footer */}
            <div className="hidden md:block">
              <LocaleSelector variant="compact" />
            </div>
            {/* Login button - visible on all screens */}
            <Link 
              to="/login" 
              className={`px-4 py-2 sm:px-5 sm:py-2.5 font-medium rounded-full transition border-2 ${
                isScrolled 
                  ? 'border-gray-200 text-gray-700 hover:border-[#1a56db] hover:text-[#1a56db]' 
                  : 'border-gray-200 text-gray-700 hover:border-[#1a56db] hover:text-[#1a56db] bg-white/80'
              }`}
            >
              {t('common.login', 'Entrar')}
            </Link>
            {/* CTA button - hidden on very small screens, shown on sm+ */}
            <Link 
              to="/subscribe?plan=free" 
              className="hidden lg:flex px-6 py-2.5 bg-[#1a56db] hover:bg-[#1648c0] text-white rounded-full font-medium transition shadow-lg shadow-blue-500/25"
              data-testid="get-started-btn"
            >
              {t('landing.cta.startFree', 'Começar Grátis')}
            </Link>
            {/* Mobile hamburger — visible below md breakpoint */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
              aria-label="Abrir menu"
              data-testid="mobile-menu-open"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-mobile-backdrop"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Panel */}
          <div className="absolute top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl flex flex-col animate-mobile-drawer">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <Logo className="h-8 w-auto" />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                aria-label="Fechar menu"
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              {[
                { href: "#funcionalidades", label: t('landing.nav.features', 'Funcionalidades') },
                { href: "#como-funciona", label: t('landing.nav.howItWorks', 'Como Funciona') },
                { href: "#galeria", label: "Galeria" },
                { href: "#testemunhos", label: t('landing.nav.testimonials', 'Testemunhos') },
                { href: "#precos", label: t('landing.nav.pricing', 'Preços') },
              ].map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-4 text-lg font-medium text-gray-800 hover:bg-blue-50 hover:text-[#1a56db] border-b border-gray-50 transition"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="p-4 border-t border-gray-100 space-y-3">
              <Link
                to="/subscribe?plan=free"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center px-6 py-3.5 bg-[#1a56db] hover:bg-[#1648c0] text-white rounded-full font-semibold shadow-lg shadow-blue-500/25"
              >
                {t('landing.cta.startFree', 'Começar Grátis')}
              </Link>
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center px-6 py-3 border-2 border-gray-200 rounded-full font-medium text-gray-700 hover:border-[#1a56db] hover:text-[#1a56db]"
              >
                {t('common.login', 'Entrar')}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        
        <div className="container mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left lg:pl-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-[#1a56db] rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                {hero?.badge || t('landing.hero.badge', 'Da placa imobiliária ao cliente em segundos')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {hero?.title_1 || t('landing.hero.title1', 'Conectamos')} <span className="text-[#1a56db]">{hero?.title_highlight || t('landing.hero.title2', 'Placas Imobiliárias')}</span> {hero?.title_2 || t('landing.hero.title3', 'a')} <span className="italic">{hero?.title_italic || t('landing.hero.title4', 'Pessoas Interessadas')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-xl">
                {hero?.subtitle || t('landing.hero.subtitle', 'A única solução que liga o imóvel físico ao cliente interessado. Com o IMOCODE, cada placa imobiliária torna-se uma porta digital para captar contactos e fechar negócios.')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to="/subscribe?plan=free" 
                  className="px-8 py-4 bg-[#1a56db] hover:bg-[#1648c0] text-white rounded-full font-semibold text-lg transition shadow-xl shadow-blue-500/30 flex items-center justify-center gap-2"
                >
                  {hero?.cta_primary_label || t('landing.cta.startFreeFull', 'Começar Gratuitamente')}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <button
                  type="button"
                  onClick={() => setShowDemoModal(true)}
                  className={`px-8 py-4 bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 rounded-full font-semibold text-lg transition flex items-center justify-center gap-2 ${
                    demoVideoEnabled ? '' : 'hidden'
                  }`}
                  data-testid="watch-demo-btn"
                >
                  <Play className="w-5 h-5" />
                  {t('landing.cta.watchDemo', 'Ver Demo')}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-6 flex items-center justify-center lg:justify-start gap-2">
                <Check className="w-4 h-4 text-green-500" />
                {hero?.no_credit_card_text || t('landing.hero.noCreditCard', 'Sem cartão de crédito • Cancele a qualquer momento')}
              </p>
            </div>
            
            {/* Right Content - Phone Mockup (animated) */}
            <HeroPhoneMockup />
          </div>
        </div>
      </section>

      {/* Trusted By / Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <p className="text-center text-gray-400 mb-8 font-medium tracking-wide text-sm uppercase">A solução nº1 para transformar placas imobiliárias em leads</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <p className="text-3xl md:text-4xl font-bold text-[#1a56db]">{stat.value}</p>
                <p className="text-gray-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section — modern zig-zag layout with illustrative images */}
      <Suspense fallback={<div className="min-h-[600px]" aria-hidden="true" />}>
        <FeaturesShowcase cards={featureCards} header={featuresHeader} />
      </Suspense>

      {/* Kit Campaign Section - rendered only when admin enables it via /api/admin/kit-campaign/settings */}
      <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
        <KitCampaignSection />
      </Suspense>

      {/* Where to Use - Image Gallery Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#1a56db] font-semibold text-sm uppercase tracking-wider">Onde Usar</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Em qualquer tipo de <span className="italic">imóvel</span>, em qualquer placa
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Apartamentos, moradias, terrenos, escritórios, lojas. Vende-se, aluga-se ou trespassa-se. O IMOCODE funciona em qualquer placa — basta colar o QR.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {(showcaseCards || []).map((card, idx) => {
              const colorMap = {
                orange: { bg: "bg-orange-500", text: "text-orange-300", icon: Building2 },
                green: { bg: "bg-green-600", text: "text-green-300", icon: Home },
                amber: { bg: "bg-amber-600", text: "text-amber-300", icon: MapPin },
                blue: { bg: "bg-blue-600", text: "text-blue-300", icon: Building2 },
              };
              const c = colorMap[card.badge_color] || colorMap.orange;
              const CategoryIcon = c.icon;
              const rotation =
                idx === 0 ? "-rotate-2" : idx === 1 ? "rotate-1" : "-rotate-1";
              return (
                <div
                  key={idx}
                  className="group relative rounded-3xl overflow-hidden shadow-xl"
                  data-testid={`showcase-card-${idx}`}
                >
                  <img
                    src={card.image_url}
                    alt={card.title}
                    className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className={`absolute top-4 left-4 ${c.bg} text-white px-4 py-2 rounded-lg shadow-lg transform ${rotation}`}>
                    <p className="font-bold text-lg">{card.badge_text}</p>
                    {card.subtitle && (
                      <p className="text-xs opacity-90">{card.subtitle}</p>
                    )}
                  </div>
                  <div className="absolute bottom-0 left-0 p-6 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <CategoryIcon className="w-5 h-5" />
                      <span className={`text-sm font-medium ${c.text}`}>
                        {card.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold">{card.title}</h3>
                    <p className="text-sm text-gray-300 mt-1">{card.description}</p>
                  </div>
                  <div className="absolute top-4 right-4 bg-white p-2 rounded-xl shadow-lg">
                    <QrCode className="w-8 h-8 text-[#1a56db]" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Phone Scanning Demo */}
          <div className="mt-16 bg-white rounded-3xl p-8 md:p-12 shadow-xl max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
                  <Phone className="w-4 h-4" />
                  Leitura Instantânea
                </span>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Basta apontar a câmara do telemóvel
                </h3>
                <p className="text-gray-600 mb-6">
                  Sem aplicação, sem registo, sem fricção. O potencial comprador aponta a câmara do telemóvel à placa e em 2 segundos tem o imóvel à frente — com fotos, preço e botão para falar consigo.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-700">
                    <Check className="w-5 h-5 text-green-500" />
                    Sem instalar app — funciona com a câmara nativa
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <Check className="w-5 h-5 text-green-500" />
                    iOS, Android, Windows ou Huawei — todos lêem
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <Check className="w-5 h-5 text-green-500" />
                    Carregamento em menos de 2 segundos, mesmo com 4G fraco
                  </li>
                </ul>
              </div>
              <div className="relative flex justify-center">
                <div className="relative">
                  {/* Hand with phone illustration */}
                  <div className="w-48 h-96 bg-gradient-to-b from-gray-800 to-gray-900 rounded-[2.5rem] p-2 shadow-2xl transform rotate-6">
                    <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden">
                      <div className="h-full bg-gradient-to-b from-blue-50 to-white p-4 flex flex-col items-center justify-center">
                        <div className="w-24 h-24 border-4 border-[#1a56db] rounded-xl mb-4 flex items-center justify-center">
                          <QrCode className="w-16 h-16 text-[#1a56db]" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 text-center">A escanear...</p>
                        <div className="mt-4 w-full bg-gray-100 rounded-xl p-3">
                          <p className="text-xs text-gray-500 mb-1">A carregar</p>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-[#1a56db] rounded-full w-3/4 animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Floating property card — featured Portuguese listing (Lisbon rooftops) */}
                  <div className="absolute -right-16 top-20 bg-white rounded-2xl p-4 shadow-xl w-48 animate-bounce" style={{animationDuration: '2s'}}>
                    <img
                      src="https://images.unsplash.com/photo-1653580978823-2f087049fa04?w=400&h=200&fit=crop&q=80"
                      alt="Apartamento T3 no Chiado, Lisboa"
                      className="w-full h-20 object-cover rounded-lg mb-2"
                      loading="lazy"
                    />
                    <p className="font-bold text-gray-900 text-sm">T3 Chiado, Lisboa</p>
                    <p className="text-[#1a56db] font-bold">€850.000</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section — bigger, richer, more intuitive */}
      <section id="como-funciona" className="py-32 bg-gradient-to-b from-gray-50 via-blue-50/40 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20 max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-100 text-[#1a56db] rounded-full text-sm font-semibold uppercase tracking-wider">
              Como Funciona
            </span>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mt-5 mb-5 leading-tight">
              Do registo ao primeiro lead em{" "}
              <span className="bg-gradient-to-r from-[#1a56db] to-purple-600 bg-clip-text text-transparent italic">
                menos de 24 horas
              </span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              5 passos. Zero complicações técnicas. Pensado para agentes imobiliários — não para programadores.
            </p>
          </div>

          <div className="space-y-16 max-w-6xl mx-auto">
            {(howItWorks || []).map((step, idx) => {
              const reversed = idx % 2 === 1;
              const accentMap = {
                blue:    "from-blue-500 to-indigo-600",
                emerald: "from-emerald-500 to-teal-600",
                amber:   "from-amber-500 to-orange-600",
                violet:  "from-violet-500 to-purple-600",
                rose:    "from-rose-500 to-pink-600",
                teal:    "from-teal-500 to-cyan-600",
              };
              const accent = accentMap[step.accent] || accentMap.blue;
              const stepIcons = [QrCode, Building2, Users];
              const Icon = stepIcons[idx] || QrCode;
              return (
                <div
                  key={step.num || idx}
                  data-testid={`how-it-works-step-${idx}`}
                  className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                    reversed ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  {/* Image card */}
                  <div className="relative group">
                    <div
                      className={`absolute -inset-4 bg-gradient-to-r ${accent} rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition`}
                    ></div>
                    <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-gray-100">
                      {step.image_url ? (
                        <img
                          src={step.image_url}
                          alt={step.title}
                          loading="lazy"
                          className="w-full h-[320px] md:h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className={`w-full h-[320px] md:h-[400px] bg-gradient-to-br ${accent}`} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent"></div>
                      {step.badge && (
                        <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full shadow-lg">
                          <span
                            className={`w-2 h-2 rounded-full bg-gradient-to-r ${accent} animate-pulse`}
                          ></span>
                          <span className="text-xs font-semibold text-gray-800">
                            {step.badge}
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-5 right-5 bg-white rounded-2xl p-4 shadow-xl">
                        <div
                          className={`w-12 h-12 bg-gradient-to-br ${accent} rounded-xl flex items-center justify-center`}
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={reversed ? "lg:pr-4" : "lg:pl-4"}>
                    <div className="flex items-center gap-3 mb-4">
                      <span
                        className={`text-6xl font-extrabold bg-gradient-to-br ${accent} bg-clip-text text-transparent leading-none`}
                      >
                        {step.num || String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="h-12 w-px bg-gray-200"></span>
                      <span
                        className={`text-sm font-semibold uppercase tracking-wider bg-gradient-to-r ${accent} bg-clip-text text-transparent`}
                      >
                        Passo {idx + 1} de {howItWorks.length}
                      </span>
                    </div>
                    <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-lg text-gray-600 mb-7 leading-relaxed">
                      {step.description}
                    </p>
                    {step.bullets && step.bullets.length > 0 && (
                      <ul className="space-y-3 mb-2">
                        {step.bullets.map((b, bi) => (
                          <li key={bi} className="flex items-start gap-3">
                            <span
                              className={`flex-shrink-0 w-6 h-6 mt-0.5 bg-gradient-to-br ${accent} rounded-full flex items-center justify-center shadow`}
                            >
                              <Check
                                className="w-3.5 h-3.5 text-white"
                                strokeWidth={3}
                              />
                            </span>
                            <span className="text-gray-700">{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA in section */}
          <div className="mt-20 text-center">
            <Link
              to="/subscribe?plan=free"
              className="inline-flex items-center gap-2 px-10 py-5 bg-[#1a56db] hover:bg-[#1648c0] text-white rounded-full font-semibold text-lg transition shadow-xl shadow-blue-500/30"
            >
              Começar Agora
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-sm text-gray-500 mt-3">
              Sem cartão · Cancela quando quiseres · Suporte em português
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Section moved to AFTER Pricing section */}

      {/* Testimonials Section */}
      <section id="testemunhos" className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Testemunhos</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              Agentes que já triplicaram as <span className="italic">leads das placas</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div
              className="bg-gray-800/50 rounded-3xl backdrop-blur-sm border border-gray-700 relative overflow-hidden"
              onMouseEnter={() => setAutoRotatePaused(true)}
              onMouseLeave={() => setAutoRotatePaused(false)}
              data-testid="testimonials-carousel"
            >
              {/* Auto-rotate progress bar */}
              {testimonials.length > 1 && !autoRotatePaused && (
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gray-700 z-10">
                  <div
                    key={activeTestimonial}
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                    style={{
                      animation: "testimonialProgress 3s linear forwards",
                    }}
                  />
                </div>
              )}

              {/* Previous arrow */}
              {testimonials.length > 1 && (
                <button
                  onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                  aria-label="Testemunho anterior"
                  className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 bg-gray-900/60 hover:bg-gray-900 text-white rounded-full backdrop-blur transition-all"
                >
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              )}

              {/* Slide track */}
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-700 ease-out"
                  style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                >
                  {testimonials.map((tm, idx) => (
                    <div
                      key={idx}
                      className="w-full flex-shrink-0 p-4 sm:p-6 md:p-12"
                      aria-hidden={idx !== activeTestimonial}
                    >
                      {/* Stars */}
                      <div className="flex justify-center gap-1 mb-6">
                        {[1,2,3,4,5].map(i => (
                          <Star
                            key={i}
                            className={`w-6 h-6 ${i <= (tm.rating || 5) ? "text-yellow-400 fill-current" : "text-gray-600"}`}
                          />
                        ))}
                      </div>

                      {/* Quote */}
                      <p className="text-xl md:text-2xl text-center text-gray-200 leading-relaxed mb-8 italic">
                        "{tm.text}"
                      </p>

                      {/* Author */}
                      <div className="flex items-center justify-center gap-4">
                        {tm.image ? (
                          <img
                            src={tm.image}
                            alt={tm.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-blue-400"
                            loading="lazy"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-blue-400/20 border-2 border-blue-400 flex items-center justify-center text-blue-200 font-bold">
                            {tm.name?.[0] || "?"}
                          </div>
                        )}
                        <div className="text-left">
                          <p className="font-bold text-white">{tm.name}</p>
                          <p className="text-gray-400 text-sm">{tm.role}</p>
                          <p className="text-blue-400 text-sm">{tm.company}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next arrow */}
              {testimonials.length > 1 && (
                <button
                  onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                  aria-label="Próximo testemunho"
                  className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 bg-gray-900/60 hover:bg-gray-900 text-white rounded-full backdrop-blur transition-all"
                >
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              )}

              {/* Navigation dots */}
              <div className="flex justify-center gap-2 pb-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setActiveTestimonial(idx); }}
                    aria-label={`Testemunho ${idx + 1}`}
                    className={`h-3 rounded-full transition-all ${activeTestimonial === idx ? 'bg-[#1a56db] w-8' : 'bg-gray-600 hover:bg-gray-500 w-3'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#1a56db] font-semibold text-sm uppercase tracking-wider">{t('landingPlans.sectionLabel')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              {t('landingPlans.title')} <span className="italic">{t('landingPlans.titleHighlight')}</span>
            </h2>
            <p className="text-gray-600 mb-8">{t('landingPlans.subtitle')}</p>
            
            {/* Billing Period Toggle */}
            <div className="inline-flex bg-gray-100 rounded-full p-1.5 gap-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2.5 rounded-full font-medium transition ${
                  billingPeriod === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('landingPlans.monthly')}
              </button>
              <button
                onClick={() => setBillingPeriod('semiannual')}
                className={`px-6 py-2.5 rounded-full font-medium transition relative ${
                  billingPeriod === 'semiannual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('landingPlans.semiannual')}
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  -10%
                </span>
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-6 py-2.5 rounded-full font-medium transition relative ${
                  billingPeriod === 'annual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('landingPlans.annual')}
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  {t('landingPlans.twoMonthsFree')}
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto items-stretch">
            {(() => { return null; })()}
            {/* Free Plan */}
            <div className="relative bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-gray-200 transition flex flex-col h-full">
              {(() => {
                // Round promo sticker overlaid on top-right corner. Rendered only
                // while the kit campaign is enabled AND admin provided a badge text.
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                const txt = (kitCampaign?.pricing_card_badge_text || "").trim();
                if (!active || !txt) return null;
                return (
                  <div className="absolute -top-3 -right-3 w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 text-white text-[11px] font-extrabold flex items-center justify-center shadow-xl shadow-orange-500/40 rotate-[-12deg] hover:rotate-0 transition animate-pulse z-20 text-center leading-tight px-1 border-[3px] border-white uppercase">
                    {txt}
                  </div>
                );
              })()}
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('landingPlans.free.name')}</h3>
              <div className="mb-6 min-h-[88px]">
                <span className="text-4xl font-bold text-gray-900">{getCurrencySymbol()}0</span>
                <span className="text-gray-500">/{t('landingPlans.free.period')}</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.qr1')}</li>
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.virtualCard')}</li>
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.leadCapture')}</li>
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.aiChat')}</li>
                <li className="flex items-center gap-2 text-gray-400"><X className="w-5 h-5" /> {t('landingPlans.features.brandingVisible')}</li>
              </ul>
              {(() => {
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                if (!active) return null;
                const q = kitCampaign?.free_kits_per_plan?.free ?? 0;
                const priceCents = kitCampaign?.free_plan_price_cents ?? 500;
                const obs = kitCampaign?.plan_observations?.free || "";
                const label = q > 0
                  ? <span>🎁 <strong>{q} Kit{q !== 1 ? 's' : ''} grátis</strong> · envio incluído</span>
                  : <span>🎁 <strong>1 Kit Tag Dinâmica</strong> por {getCurrencySymbol()}{(priceCents/100).toFixed(0)} · envio incluído</span>;
                return (
                  <>
                    <div className="flex items-start gap-2 mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                      <Gift className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="leading-tight">
                        {label}
                        {obs && <div className="text-[11px] text-amber-700/80 mt-0.5">{obs}</div>}
                      </div>
                    </div>
                    <button onClick={() => setShowKitExample(true)} className="text-xs text-amber-700 hover:text-amber-900 underline flex items-center gap-1 mb-3" data-testid="see-kit-example-free">
                      <Eye className="w-3 h-3" /> Ver exemplo do kit
                    </button>
                  </>
                );
              })()}
              <Link to="/subscribe?plan=free" className="block w-full py-3 text-center border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition">
                {t('landingPlans.startFree')}
              </Link>
            </div>

            {/* Starter Plan */}
            <div className="relative bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-gray-200 transition flex flex-col h-full">
              {(() => {
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                const txt = (kitCampaign?.pricing_card_badge_text || "").trim();
                if (!active || !txt) return null;
                return (
                  <div className="absolute -top-3 -right-3 w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-500 text-white text-[11px] font-extrabold flex items-center justify-center shadow-xl shadow-emerald-500/40 rotate-[-12deg] hover:rotate-0 transition animate-pulse z-20 text-center leading-tight px-1 border-[3px] border-white uppercase">
                    {txt}
                  </div>
                );
              })()}
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('landingPlans.starter.name')}</h3>
              <div className="mb-6 min-h-[88px]">
                <div>
                  <span className="text-4xl font-bold text-gray-900">
                    {formatPrice(billingPeriod === 'monthly' ? 9.90 : billingPeriod === 'semiannual' ? 53.46 : 99.00)}
                  </span>
                  <span className="text-gray-500">/{billingPeriod === 'monthly' ? t('landingPlans.perMonth') : billingPeriod === 'semiannual' ? t('landingPlans.per6Months') : t('landingPlans.perYear')}</span>
                </div>
                {billingPeriod !== 'monthly' && (
                  <p className="text-sm text-gray-500 mt-2">
                    {formatPrice(billingPeriod === 'semiannual' ? 8.91 : 8.25)}/{t('landingPlans.perMonth')}
                    {billingPeriod === 'semiannual' && <span className="text-green-600 ml-2">{t('landingPlans.save')} {formatPrice(5.94)}</span>}
                    {billingPeriod === 'annual' && <span className="text-green-600 ml-2">{t('landingPlans.save')} {formatPrice(19.80)}</span>}
                  </p>
                )}
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.qr6')}</li>
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.virtualCard')}</li>
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.unlimitedLeads')}</li>
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.basicStats')}</li>
                <li className="flex items-center gap-2 text-gray-600"><Check className="w-5 h-5 text-green-500" /> {t('landingPlans.features.noBranding')}</li>
              </ul>
              {(() => {
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                if (!active) return null;
                const q = kitCampaign?.free_kits_per_plan?.starter ?? 6;
                const obs = kitCampaign?.plan_observations?.starter || "";
                return (
                  <>
                    <div className="flex items-start gap-2 mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
                      <Gift className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="leading-tight">
                        🎁 <strong>{q} Kit{q !== 1 ? 's' : ''}</strong> incluído{q !== 1 ? 's' : ''} no plano · 1 por QR code
                        {obs && <div className="text-[11px] text-emerald-700/80 mt-0.5">{obs}</div>}
                      </div>
                    </div>
                    <button onClick={() => setShowKitExample(true)} className="text-xs text-emerald-700 hover:text-emerald-900 underline flex items-center gap-1 mb-3" data-testid="see-kit-example-starter">
                      <Eye className="w-3 h-3" /> Ver exemplo do kit
                    </button>
                  </>
                );
              })()}
              <Link to="/subscribe?plan=starter" className="block w-full py-3 text-center border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition">
                {t('landingPlans.chooseStarter')}
              </Link>
            </div>

            {/* Pro Plan - Popular */}
            <div className="bg-[#1a56db] rounded-3xl p-8 text-white shadow-[0_25px_60px_-15px_rgba(26,86,219,0.55)] ring-4 ring-blue-300/20 relative flex flex-col h-full z-10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-yellow-400 text-gray-900 text-sm font-bold rounded-full shadow-lg shadow-yellow-500/30 whitespace-nowrap">
                ⭐ {t('landingPlans.mostPopular')}
              </div>
              {(() => {
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                const txt = (kitCampaign?.pricing_card_badge_text || "").trim();
                if (!active || !txt) return null;
                return (
                  <div className="absolute -top-3 -right-3 w-20 h-20 rounded-full bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-500 text-gray-900 text-[11px] font-extrabold flex items-center justify-center shadow-xl shadow-yellow-500/60 rotate-[-12deg] hover:rotate-0 transition animate-pulse z-20 text-center leading-tight px-1 border-[3px] border-white uppercase">
                    {txt}
                  </div>
                );
              })()}
              <h3 className="text-xl font-bold mb-2">{t('landingPlans.pro.name')}</h3>
              <div className="mb-6 min-h-[88px]">
                <div>
                  <span className="text-4xl font-bold">
                    {formatPrice(billingPeriod === 'monthly' ? 29.90 : billingPeriod === 'semiannual' ? 161.46 : 299.00)}
                  </span>
                  <span className="text-blue-200">/{billingPeriod === 'monthly' ? t('landingPlans.perMonth') : billingPeriod === 'semiannual' ? t('landingPlans.per6Months') : t('landingPlans.perYear')}</span>
                </div>
                {billingPeriod !== 'monthly' && (
                  <p className="text-sm text-blue-200 mt-2">
                    {formatPrice(billingPeriod === 'semiannual' ? 26.91 : 24.92)}/{t('landingPlans.perMonth')}
                    {billingPeriod === 'semiannual' && <span className="text-yellow-300 ml-2">{t('landingPlans.save')} {formatPrice(17.94)}</span>}
                    {billingPeriod === 'annual' && <span className="text-yellow-300 ml-2">{t('landingPlans.save')} {formatPrice(59.80)}</span>}
                  </p>
                )}
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-blue-200" /> {t('landingPlans.features.qr21')}</li>
                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-blue-200" /> {t('landingPlans.features.virtualCard')}</li>
                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-blue-200" /> {t('landingPlans.features.unlimitedLeads')}</li>
                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-blue-200" /> {t('landingPlans.features.advancedStats')}</li>
                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-blue-200" /> {t('landingPlans.features.noBranding')}</li>
                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-blue-200" /> {t('landingPlans.features.dataExport')}</li>
                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-blue-200" /> {t('landingPlans.features.prioritySupport')}</li>
              </ul>
              {(() => {
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                if (!active) return null;
                const q = kitCampaign?.free_kits_per_plan?.pro ?? 21;
                const obs = kitCampaign?.plan_observations?.pro || "";
                return (
                  <>
                    <div className="flex items-start gap-2 mb-2 px-3 py-2 bg-yellow-400/20 border border-yellow-300/40 rounded-xl text-xs text-yellow-100">
                      <Gift className="w-3.5 h-3.5 text-yellow-300 flex-shrink-0 mt-0.5" />
                      <div className="leading-tight">
                        🎁 <strong>{q} Kit{q !== 1 ? 's' : ''}</strong> incluído{q !== 1 ? 's' : ''} no plano · escolhe o template
                        {obs && <div className="text-[11px] text-yellow-200/80 mt-0.5">{obs}</div>}
                      </div>
                    </div>
                    <button onClick={() => setShowKitExample(true)} className="text-xs text-yellow-200 hover:text-white underline flex items-center gap-1 mb-3" data-testid="see-kit-example-pro">
                      <Eye className="w-3 h-3" /> Ver exemplo do kit
                    </button>
                  </>
                );
              })()}
              <Link to="/subscribe?plan=pro" className="block w-full py-3 text-center bg-white text-[#1a56db] rounded-full font-semibold hover:bg-blue-50 transition">
                {t('landingPlans.startNow')}
              </Link>
            </div>

            {/* Agency Plan */}
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 text-white border-2 border-gray-700 flex flex-col h-full">
              {(() => {
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                const txt = (kitCampaign?.pricing_card_badge_text || "").trim();
                if (!active || !txt) return null;
                return (
                  <div className="absolute -top-3 -right-3 w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-600 text-white text-[11px] font-extrabold flex items-center justify-center shadow-xl shadow-indigo-500/50 rotate-[-12deg] hover:rotate-0 transition animate-pulse z-20 text-center leading-tight px-1 border-[3px] border-white uppercase">
                    {txt}
                  </div>
                );
              })()}
              <h3 className="text-xl font-bold mb-2">{t('landingPlans.agency.name')}</h3>
              <div className="mb-6 min-h-[88px]">
                <span className="text-2xl font-bold">{t('landingPlans.agency.consultation')}</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center gap-2 text-gray-300"><Check className="w-5 h-5 text-blue-400" /> {t('landingPlans.features.customQR')}</li>
                <li className="flex items-center gap-2 text-gray-300"><Check className="w-5 h-5 text-blue-400" /> {t('landingPlans.features.virtualCards')}</li>
                <li className="flex items-center gap-2 text-gray-300"><Check className="w-5 h-5 text-blue-400" /> {t('landingPlans.features.multiUsers')}</li>
                <li className="flex items-center gap-2 text-gray-300"><Check className="w-5 h-5 text-blue-400" /> {t('landingPlans.features.agencyPanel')}</li>
                <li className="flex items-center gap-2 text-gray-300"><Check className="w-5 h-5 text-blue-400" /> {t('landingPlans.features.configurableQuota')}</li>
                <li className="flex items-center gap-2 text-gray-300"><Check className="w-5 h-5 text-blue-400" /> {t('landingPlans.features.whiteLabel')}</li>
                <li className="flex items-center gap-2 text-gray-300"><Check className="w-5 h-5 text-blue-400" /> {t('landingPlans.features.dedicatedManager')}</li>
              </ul>
              {(() => {
                const active = !!(kitCampaign && (kitCampaign.is_active ?? kitCampaign.enabled));
                if (!active) return null;
                const q = kitCampaign?.free_kits_per_plan?.agency ?? 999;
                const obs = kitCampaign?.plan_observations?.agency || "";
                const label = q >= 999
                  ? <span>🎁 <strong>Kits ilimitados</strong> + co-branding</span>
                  : <span>🎁 <strong>{q} Kits</strong> + co-branding</span>;
                return (
                  <>
                    <div className="flex items-start gap-2 mb-2 px-3 py-2 bg-blue-900/40 border border-blue-700/50 rounded-xl text-xs text-blue-100">
                      <Gift className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="leading-tight">
                        {label}
                        {obs && <div className="text-[11px] text-blue-200/80 mt-0.5">{obs}</div>}
                      </div>
                    </div>
                    <button onClick={() => setShowKitExample(true)} className="text-xs text-blue-300 hover:text-white underline flex items-center gap-1 mb-3" data-testid="see-kit-example-agency">
                      <Eye className="w-3 h-3" /> Ver exemplo do kit
                    </button>
                  </>
                );
              })()}
              <button onClick={() => setShowSalesModal(true)} className="block w-full py-3 text-center bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition">
                {t('landingPlans.contactSales')}
              </button>
            </div>
          </div>
          
          {/* IVA Notice */}
          <p className="text-center mt-8 text-sm text-gray-500">
            {t('landingPlans.vatNotice')}
          </p>
        </div>
      </section>

      {/* Gallery Section - Real cases (moved here, after Pricing) */}
      <Suspense fallback={<div className="min-h-[500px]" aria-hidden="true" />}>
        <GallerySection />
      </Suspense>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#1a56db] font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Perguntas <span className="italic">Frequentes</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
              >
                <button 
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${activeFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {activeFaq === idx && (
                  <div className="px-6 pb-5 text-gray-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-center mt-8 text-gray-600">
            Ainda tem dúvidas? <a href="mailto:suporte@imocode.pt" className="text-[#1a56db] font-semibold hover:underline">Fale connosco</a>
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#1a56db] via-[#2563eb] to-[#3b82f6] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="container mx-auto px-6 text-center relative">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            A sua placa imobiliária agora trabalha para si
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Transforme a placa em leads, hoje. Comece grátis, sem cartão de crédito — e descubra quantos contactos perdeu por não ter um QR Code.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/subscribe?plan=free" 
              className="px-10 py-4 bg-white text-[#1a56db] rounded-full font-bold text-lg hover:bg-blue-50 transition shadow-xl flex items-center justify-center gap-2"
            >
              Começar Gratuitamente
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button 
              onClick={() => setShowSalesModal(true)}
              className="px-10 py-4 bg-white/10 text-white border-2 border-white/30 rounded-full font-bold text-lg hover:bg-white/20 transition flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Falar com Vendas
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Logo className="h-10 w-auto mb-4" white />
              <p className="text-sm leading-relaxed">
                Da placa imobiliária ao cliente em segundos. A plataforma de QR Codes inteligentes para agentes e agências imobiliárias em Portugal.
              </p>
              <div className="flex gap-3 mt-4">
                <a href="https://www.instagram.com/imocode.app" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 transition">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Produto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#funcionalidades" className="hover:text-white transition">Funcionalidades</a></li>
                <li><a href="#precos" className="hover:text-white transition">Preços</a></li>
                <li><a href="#como-funciona" className="hover:text-white transition">Como Funciona</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/termos" className="hover:text-white transition">Termos e Condições</Link></li>
                <li><Link to="/privacidade" className="hover:text-white transition">Política de Privacidade</Link></li>
                <li><a href="https://www.livroreclamacoes.pt/inicio/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Livro de Reclamações</a></li>
                <li><Link to="/contactos" className="hover:text-white transition">Contactos</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Newsletter</h4>
              <p className="text-sm mb-4">Receba dicas e novidades no seu email.</p>
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  const email = e.target.newsletter_email.value;
                  if (email) {
                    try {
                      const response = await axios.post(`${API}/newsletters/subscribe`, { email });
                      toast.success(response.data.message || 'Obrigado por subscrever!');
                      e.target.reset();
                    } catch (error) {
                      toast.error('Erro ao subscrever. Tente novamente.');
                    }
                  }
                }}
                className="space-y-3"
              >
                <input
                  type="email"
                  name="newsletter_email"
                  placeholder="O seu email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                <button
                  type="submit"
                  className="w-full px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition text-sm"
                >
                  Subscrever
                </button>
              </form>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Locale Selector — mobile only; desktop has it in the navbar */}
            <div className="md:hidden flex flex-col items-center gap-2 w-full pb-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Idioma e moeda</p>
              <LocaleSelector variant="compact" className="bg-gray-800 border border-gray-700 text-gray-200" />
            </div>
            <p className="text-sm">© 2026 IMOCODE. Todos os direitos reservados. Cardume Pioneiro - Serviços Digitais, Lda.</p>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/imocode.app" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 transition">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sales Lead Modal */}
      {showDemoModal && <DemoVideoModal onClose={() => setShowDemoModal(false)} />}

      {/* Kit Example Modal */}
      {showKitExample && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowKitExample(false)}
          data-testid="kit-example-modal"
        >
          <div
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 p-8 text-white rounded-t-3xl">
              <button
                onClick={() => setShowKitExample(false)}
                className="absolute top-4 right-4 w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3 mb-2">
                <Gift className="w-7 h-7 text-yellow-300" />
                <h3 className="text-2xl md:text-3xl font-bold">O que vem dentro do teu Kit?</h3>
              </div>
              <p className="text-white/80 text-sm">
                Veja exatamente o que recebe em casa ao subscrever ou pedir o Kit Tag Dinâmica.
              </p>
            </div>

            <div className="p-6 md:p-8">
              {/* Visual: real media if admin uploaded, otherwise CSS mockup */}
              <div className="relative aspect-[16/9] bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl overflow-hidden mb-6 border border-amber-100">
                {kitMedia.video ? (
                  <video
                    src={kitMedia.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    data-testid="kit-example-video"
                  />
                ) : kitMedia.image ? (
                  <img
                    src={kitMedia.image}
                    alt="Exemplo do Kit Tag Dinâmica"
                    loading="lazy"
                    className="w-full h-full object-cover"
                    data-testid="kit-example-image"
                  />
                ) : (
                  <>
                    {/* Fallback CSS mockup when admin hasn't uploaded media yet */}
                    <div className="absolute left-[6%] top-[18%] w-[28%] aspect-square bg-white rounded-2xl shadow-2xl flex items-center justify-center transform rotate-[-6deg]">
                      <div className="w-[70%] aspect-square bg-gradient-to-br from-gray-900 to-gray-700 rounded-lg grid grid-cols-6 grid-rows-6 gap-[2px] p-2">
                        {Array.from({ length: 36 }).map((_, i) => (
                          <div
                            key={i}
                            className={`rounded-sm ${(i * 7) % 3 === 0 ? "bg-white" : "bg-gray-900"}`}
                          ></div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute left-[19%] top-[28%] w-[28%] aspect-square bg-white rounded-2xl shadow-xl flex items-center justify-center transform rotate-[3deg]">
                      <div className="w-[70%] aspect-square bg-gradient-to-br from-blue-700 to-indigo-700 rounded-lg grid grid-cols-6 grid-rows-6 gap-[2px] p-2">
                        {Array.from({ length: 36 }).map((_, i) => (
                          <div
                            key={i}
                            className={`rounded-sm ${(i * 13) % 4 === 0 ? "bg-white" : "bg-indigo-900"}`}
                          ></div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute right-[6%] top-[14%] w-[36%] aspect-[3/4] bg-white rounded-2xl shadow-2xl p-3 transform rotate-[5deg]">
                      <div className="h-3 bg-pink-500 rounded-full mb-2 w-2/3"></div>
                      <div className="space-y-1.5">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div
                            key={i}
                            className="h-1.5 bg-gray-200 rounded-full"
                            style={{ width: `${60 + ((i * 11) % 35)}%` }}
                          ></div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-yellow-400 text-gray-900 text-xs font-bold rounded-full shadow-lg flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      Mockup ilustrativo
                    </div>
                  </>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {[
                  {
                    title: "Tags físicas resistentes",
                    desc: "Plástico standard ou acrílico premium · 1 tag por cada QR code do plano",
                    color: "from-amber-400 to-orange-500",
                  },
                  {
                    title: "Códigos QR únicos",
                    desc: "Cada tag traz QR pré-codificado e ID exclusivo para análise",
                    color: "from-pink-500 to-rose-500",
                  },
                  {
                    title: "Manual de boas-práticas",
                    desc: "Onde colar, como divulgar, scripts de conversão",
                    color: "from-purple-500 to-indigo-500",
                  },
                  {
                    title: "Cartão de boas-vindas",
                    desc: "Personalizado com o teu nome e ligação ao painel",
                    color: "from-emerald-500 to-teal-500",
                  },
                ].map((it) => (
                  <div
                    key={it.title}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-2xl border border-gray-100"
                  >
                    <div
                      className={`w-9 h-9 rounded-xl bg-gradient-to-br ${it.color} flex items-center justify-center flex-shrink-0`}
                    >
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{it.title}</p>
                      <p className="text-xs text-gray-500">{it.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <button
                  onClick={() => setShowKitExample(false)}
                  className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition text-sm"
                >
                  Fechar
                </button>
                <a
                  href="#campanha-kit"
                  onClick={() => setShowKitExample(false)}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:opacity-95 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition shadow-lg shadow-pink-500/30"
                >
                  <Gift className="w-4 h-4" />
                  Ir para a campanha
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSalesModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSalesModal(false)}>
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Falar com Vendas</h2>
                <p className="text-gray-500 text-sm">Preencha os dados para agendarmos uma chamada</p>
              </div>
              <button onClick={() => setShowSalesModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSalesSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Nome Completo *</label>
                <input
                  type="text"
                  required
                  value={salesForm.name}
                  onChange={(e) => setSalesForm({...salesForm, name: e.target.value})}
                  placeholder="João Silva"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-[#1a56db]"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Telefone *</label>
                <input
                  type="tel"
                  required
                  value={salesForm.phone}
                  onChange={(e) => setSalesForm({...salesForm, phone: e.target.value})}
                  placeholder="+351 912 345 678"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-[#1a56db]"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={salesForm.email}
                  onChange={(e) => setSalesForm({...salesForm, email: e.target.value})}
                  placeholder="joao@exemplo.pt"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-[#1a56db]"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Região *</label>
                <input
                  type="text"
                  required
                  value={salesForm.region}
                  onChange={(e) => setSalesForm({...salesForm, region: e.target.value})}
                  placeholder="Lisboa"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-[#1a56db]"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Sou um... *</label>
                <div className="grid grid-cols-3 gap-3">
                  {['Agente Imobiliário', 'Agência', 'Particular'].map((type) => (
                    <label 
                      key={type}
                      className={`flex items-center justify-center p-3 border-2 rounded-xl cursor-pointer transition text-sm ${
                        salesForm.type === type 
                          ? 'border-[#1a56db] bg-blue-50 text-[#1a56db]' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <input 
                        type="radio" 
                        name="type" 
                        value={type}
                        checked={salesForm.type === type}
                        onChange={(e) => setSalesForm({...salesForm, type: e.target.value})}
                        className="sr-only"
                        required
                      />
                      {type}
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Melhor hora de contacto *</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'any', label: 'A qualquer hora' },
                    { value: 'morning', label: 'Manhã (9h-12h)' },
                    { value: 'afternoon', label: 'Tarde (14h-18h)' },
                    { value: 'evening', label: 'Depois das 18h' }
                  ].map((time) => (
                    <label 
                      key={time.value}
                      className={`flex items-center justify-center p-3 border-2 rounded-xl cursor-pointer transition text-sm ${
                        salesForm.contactTime === time.value 
                          ? 'border-[#1a56db] bg-blue-50 text-[#1a56db]' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <input 
                        type="radio" 
                        name="contactTime" 
                        value={time.value}
                        checked={salesForm.contactTime === time.value}
                        onChange={(e) => setSalesForm({...salesForm, contactTime: e.target.value})}
                        className="sr-only"
                        required
                      />
                      {time.label}
                    </label>
                  ))}
                </div>
              </div>
              
              <button
                type="submit"
                disabled={submittingSales}
                className="w-full py-4 bg-[#1a56db] hover:bg-[#1648c0] text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2 mt-6"
              >
                {submittingSales ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Agendar Chamada
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
              
              <p className="text-center text-gray-400 text-xs mt-4">
                Ao submeter, concorda com a nossa política de privacidade
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
